import React, { useState, useEffect } from 'react';
import { useUser } from '../UserContext';
import './Einstellungen.css';
import { getModulePermissions, updateModuleVisibility, getSidebarOrder, setSidebarOrder } from '../services/firebaseModuleService';

const defaultNavOrder = [
  { id: 'dashboard', path: '/', icon: 'fas fa-home', label: 'Dashboard', module: 'dashboard' },
  { id: 'dienstuebergabe', path: '/dienstuebergabe', icon: 'fas fa-exchange-alt', label: 'Dienstübergabe', module: 'dienstuebergabe' },
  { id: 'staerkemeldung', path: '/staerkemeldung', icon: 'fas fa-users', label: 'Stärkemeldung', module: 'staerkemeldung' },
  { id: 'aufgaben', path: '/aufgaben', icon: 'fas fa-tasks', label: 'Aufgaben', module: 'aufgaben' },
  { id: 'feuerloescher', path: '/feuerloescher', icon: 'fas fa-fire-extinguisher', label: 'Feuerlöscher', module: 'feuerloescher' },
  { id: 'engeRaeume', path: '/enge-raeume', icon: 'fas fa-door-closed', label: 'Enge Räume', module: 'engeRaeume' },
  { id: 'berichte', path: '/berichte', icon: 'fas fa-file-alt', label: 'Berichte', module: 'berichte' },
  { id: 'dienstplan', path: '/dienstplan', icon: 'fas fa-calendar-alt', label: 'Dienstplan', module: 'dienstplan' },
  { id: 'freiwuenscheUrlaub', path: '/freiwuensche-urlaub', icon: 'fas fa-calendar-check', label: 'Freiwünsche & Urlaub', module: 'freiwuenscheUrlaub' },
  { id: 'passwort', path: '/passwort', icon: 'fas fa-key', label: 'Passwort', module: 'passwort' },
  { id: 'speicherorte', path: '/speicherorte', icon: 'fas fa-save', label: 'Speicherorte', module: 'speicherorte' },
  { id: 'einstellungen', path: '/einstellungen', icon: 'fas fa-cog', label: 'Einstellungen', module: 'einstellungen' },
  { id: 'benutzerverwaltung', path: '/benutzerverwaltung', icon: 'fas fa-users-cog', label: 'Benutzerverwaltung', module: 'benutzerverwaltung', adminOnly: true }
];

const ROLES = [
  { value: 'superadmin', label: 'Superadmin' },
  { value: 'admin', label: 'Admin' },
  { value: 'user', label: 'Benutzer' }
];

const SidebarSettingsPanel = () => {
  const { user: currentUser } = useUser();
  const [isEditMode, setIsEditMode] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [navOrder, setNavOrder] = useState(defaultNavOrder);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Lade globale Reihenfolge aus Firestore
    getSidebarOrder().then(orderFromDb => {
      if (orderFromDb && Array.isArray(orderFromDb)) {
        // Sortiere defaultNavOrder nach Firestore-Array
        const sorted = orderFromDb.map(id => defaultNavOrder.find(item => item.id === id)).filter(Boolean);
        // Füge evtl. neue Menüpunkte hinten an
        const missing = defaultNavOrder.filter(item => !orderFromDb.includes(item.id));
        setNavOrder([...sorted, ...missing]);
      } else {
        setNavOrder(defaultNavOrder);
      }
    });
    getModulePermissions().then(data => {
      setPermissions(data);
      setLoading(false);
    });
  }, []);

  const hasAdminRights = () => {
    return currentUser && (currentUser.role === "admin" || currentUser.role === "superadmin");
  };

  const handleDragStart = (e, index) => {
    if (!isEditMode) return;
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    if (!isEditMode) return;
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    if (!isEditMode || dragIndex === null) return;
    e.preventDefault();
    const newOrder = [...navOrder];
    const draggedItem = newOrder[dragIndex];
    newOrder.splice(dragIndex, 1);
    newOrder.splice(dropIndex, 0, draggedItem);
    setNavOrder(newOrder);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const toggleEditMode = async () => {
    if (isEditMode) {
      // Speichere neue Reihenfolge global in Firestore
      await setSidebarOrder(navOrder.map(item => item.id));
    }
    setIsEditMode(!isEditMode);
  };

  const resetOrder = async () => {
    setNavOrder(defaultNavOrder);
    await setSidebarOrder(defaultNavOrder.map(item => item.id));
  };

  const shouldShowItem = (item) => {
    if (item.adminOnly && !hasAdminRights()) return false;
    return true;
  };

  const handleVisibilityChange = async (moduleName, role) => {
    const currentPermission = permissions[moduleName] || { enabled: true, sichtbarkeit: ['user'] };
    const currentSichtbarkeit = currentPermission.sichtbarkeit || ['user'];
    const isCurrentlyVisible = currentSichtbarkeit.includes(role);
    
    // Toggle: Wenn sichtbar, dann ausblenden; wenn unsichtbar, dann anzeigen
    await updateModuleVisibility(moduleName, role, !isCurrentlyVisible);
    
    // Lokalen State sofort aktualisieren
    setPermissions(prev => ({
      ...prev,
      [moduleName]: {
        ...prev[moduleName],
        enabled: true, // Module sind immer aktiviert
        sichtbarkeit: isCurrentlyVisible 
          ? currentSichtbarkeit.filter(r => r !== role)
          : [...currentSichtbarkeit, role]
      }
    }));
  };

  if (loading) return <div style={{padding: 32}}>Lade Modulberechtigungen...</div>;

  return (
    <div className="sidebar-settings-panel" style={{margin: '32px auto 0 auto', maxWidth: 1200, paddingLeft: 20}}>
      <div className="sidebar-settings-header">
        <span>Sidebar-Menü Einstellungen</span>
      </div>
      <div className="sidebar-settings-content">
        {hasAdminRights() ? (
          <div className="edit-controls">
            <button 
              className={`edit-mode-btn ${isEditMode ? 'active' : ''}`}
              onClick={toggleEditMode}
            >
              <i className={`fas ${isEditMode ? 'fa-check' : 'fa-edit'}`}></i>
              {isEditMode ? 'Bearbeitung beenden' : 'Reihenfolge bearbeiten'}
            </button>
            {isEditMode && (
              <button 
                className="reset-order-btn"
                onClick={resetOrder}
                title="Standard-Reihenfolge wiederherstellen"
              >
                <i className="fas fa-undo"></i> Zurücksetzen
              </button>
            )}
          </div>
        ) : (
          <div className="no-permission">
            <i className="fas fa-lock"></i>
            <p>Nur Administratoren können die Sidebar-Reihenfolge ändern.</p>
          </div>
        )}
        <div className="nav-order-list">
          {navOrder.filter(shouldShowItem).map((item, index) => {
            if(item.id === 'modulfunktionen') return null;
            const isDragging = dragIndex === index;
            const isDragOver = dragOverIndex === index;
            const moduleName = item.module || item.id;
            const modulePermission = permissions[moduleName] || { enabled: true, sichtbarkeit: ['user'] };
            const sichtbarkeit = modulePermission.sichtbarkeit || ['user'];
            return (
              <div
                key={item.id}
                className={`nav-item-card ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''}`}
                draggable={isEditMode}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                onDragEnd={handleDragEnd}
              >
                <div className="nav-item-content" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <span style={{display:'flex',alignItems:'center',gap:8, minWidth:240, maxWidth:240, fontSize:'1.08em', fontWeight:500}}>
                    <i className={item.icon}></i>
                    <span className="nav-item-label" style={{whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{item.label}</span>
                  </span>
                  <span className="sidebar-visibility-group" style={{display:'flex',alignItems:'center',gap:0,minWidth:370}}>
                    {ROLES.map((role, idx) => (
                      <span
                        key={role.value}
                        className="sidebar-visibility-option"
                        onClick={() => handleVisibilityChange(moduleName, role.value)}
                        style={{
                          cursor:'pointer',
                          display:'inline-flex',
                          alignItems:'center',
                          justifyContent:'flex-start',
                          minWidth: idx === 0 ? 150 : 110,
                          maxWidth: idx === 0 ? 150 : 110,
                          paddingLeft:0,
                          paddingRight:0
                        }}
                      >
                        <span
                          className="sidebar-visibility-dot"
                          style={{
                            display:'inline-block',
                            width:14,
                            height:14,
                            borderRadius:'50%',
                            marginRight:6,
                            background: sichtbarkeit.includes(role.value) ? '#00e676' : '#e53935',
                            boxShadow: sichtbarkeit.includes(role.value) ? '0 0 8px 2px #00e676' : '0 0 6px 1px #e53935',
                            border: '2px solid #fff',
                            transition: 'background 0.2s, box-shadow 0.2s'
                          }}
                        />
                        <span style={{fontWeight: sichtbarkeit.includes(role.value) ? 700 : 400, color: sichtbarkeit.includes(role.value) ? '#1976d2' : '#888', minWidth:60}}>{role.label}</span>
                      </span>
                    ))}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SidebarSettingsPanel; 